import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import config from '../config/config';
import { trackEvent, EVENTS } from '../utils/analytics';
import '../styles/Articles.css';

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });
  const [activeCategory, setActiveCategory] = useState('all');

  // Define category options
  const categories = [
    { id: 'all', label: 'Alle Artikel' },
    { id: 'dividend', label: 'Dividenden-Aktien' },
    { id: 'growth', label: 'Wachstumsaktien' },
    { id: 'value', label: 'Value-Investing' },
    { id: 'income', label: 'Einkommensaktien' },
    { id: 'market-analysis', label: 'Marktanalyse' }
  ];

  useEffect(() => {
    fetchArticles(pagination.page, activeCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, activeCategory]);

  const fetchArticles = async (page, category) => {
    try {
      setLoading(true);
      let url = `${config.apiUrl}/api/articles?page=${page}&limit=${pagination.limit}`;
      if (category && category !== 'all') {
        url += `&category=${category}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setArticles(data.articles);
      setPagination(prev => ({
        ...prev,
        page: data.pagination.page,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages
      }));

      // Track view
      trackEvent(EVENTS.VIEW_ARTICLES, {
        page,
        category,
        articleCount: data.articles.length
      });
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Artikel konnten nicht geladen werden. Bitte versuche es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      // Scroll to top
      window.scrollTo(0, 0);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };

  // Parse tags from JSON string
  const parseTags = (tags) => {
    if (Array.isArray(tags)) return tags;
    try {
      return JSON.parse(tags || '[]');
    } catch (e) {
      return [];
    }
  };

  // Turn a category slug like "market_analysis" into a readable label
  const formatCategory = (category) => {
    if (!category) return '';
    const known = categories.find(c => c.id === category);
    if (known) return known.label;
    return category.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Handle image error
  const handleImageError = (e) => {
    e.target.style.display = 'none';
    // Find the parent element and add a class to style it appropriately
    e.target.parentElement.classList.add('image-fallback');
  };

  return (
    <>
      <Helmet>
        <title>Aktienwissen und Marktanalysen | DynaStoxx</title>
        <meta 
          name="description" 
          content="Entdecke fundierte Analyse zu Dividendenaktien, Wachstumswerten und Value-Investing. Aktuelle Markttrends und Aktienempfehlungen."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://dynastoxx.com/articles" />
      </Helmet>

      <div className="articles-container">
        <div className="articles-header">
          <h1>Aktienwissen und Marktanalysen</h1>
          <p className="articles-description">
            Fundierte Analysen, Empfehlungen und Markteinblicke für intelligente Investitionen
          </p>
        </div>

        {/* Category Navigation */}
        <div className="categories-nav">
          {categories.map(category => (
            <button
              type="button"
              key={category.id}
              className={`category-button ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category.id)}
              aria-pressed={activeCategory === category.id}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="loading-container" role="status">
            <div className="loading-spinner"></div>
            <p>Artikel werden geladen...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div className="articles-grid">
              {articles.length > 0 ? (
                articles.map(article => (
                  <Link 
                    to={`/articles/${article.slug}`} 
                    className="article-card"
                    key={article.id}
                    onClick={() => trackEvent(EVENTS.CLICK_ARTICLE, {
                      articleId: article.id,
                      articleTitle: article.title,
                      articleCategory: article.category
                    })}
                  >
                    <div className={`article-image ${!article.thumbnail_url ? 'image-fallback' : ''}`}>
                      {article.thumbnail_url && (
                        <img 
                          src={article.thumbnail_url} 
                          alt={article.title}
                          loading="lazy"
                          onError={handleImageError}
                        />
                      )}
                      {!article.thumbnail_url && (
                        <div className="image-placeholder">
                          <i className="fas fa-newspaper"></i>
                        </div>
                      )}
                      <span className="article-category">{formatCategory(article.category)}</span>
                    </div>
                    <div className="article-content">
                      <h2 className="article-title-preview">{article.title}</h2>
                      <p className="article-description">{article.meta_description}</p>
                      <div className="article-footer">
                        <span className="article-date">{formatDate(article.published_date)}</span>
                        <div className="article-tags">
                          {parseTags(article.tags).slice(0, 2).map((tag, index) => (
                            <span key={index} className="article-tag">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="no-articles">
                  <i className="fas fa-file-alt"></i>
                  <p>Keine Artikel gefunden</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button 
                  type="button"
                  className="pagination-button"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  aria-label="Vorherige Seite"
                >
                  <i className="fas fa-chevron-left" aria-hidden="true"></i>
                </button>
                
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === pagination.totalPages || 
                    Math.abs(page - pagination.page) <= 1
                  )
                  .reduce((acc, page, i, arr) => {
                    if (i > 0 && arr[i - 1] !== page - 1) {
                      acc.push('...');
                    }
                    acc.push(page);
                    return acc;
                  }, [])
                  .map((page, index) => 
                    typeof page === 'number' ? (
                      <button
                        type="button"
                        key={index}
                        className={`pagination-button ${pagination.page === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                        aria-current={pagination.page === page ? 'page' : undefined}
                      >
                        {page}
                      </button>
                    ) : (
                      <span key={index} className="pagination-ellipsis">{page}</span>
                    )
                  )
                }
                
                <button
                  type="button"
                  className="pagination-button"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  aria-label="Nächste Seite"
                >
                  <i className="fas fa-chevron-right" aria-hidden="true"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Articles;